import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Inbox, X, ChevronDown } from "lucide-react";
import HelpdeskRail, { formatPhone } from "./HelpdeskRail";
import RetireNumberModal from "../../views/adminView/helpdesk/components/RetireNumberModal";
import {
  cachedChannelAccounts,
  fetchChannelAccounts,
} from "../../api/helpdeskChannelAccounts";
import usePersistentState from "../../hooks/usePersistentState";
// A6 · Hoja mobile de la suite (clase .hd-brand-solid). Se importa desde el
// shell y no desde cada vista para que viaje en el mismo chunk que las 6 rutas.
import "../../components/helpdesk/helpdeskMobile.scss";

// A6 · Etiquetas legibles de cada bandeja/sección, para que la barra móvil diga
// DÓNDE estás en vez de ser una hamburguesa muda. Mismo vocabulario que el rail
// (HelpdeskRail) y que la cabecera de la lista (ConversationList → INBOX_TITLE):
// una sola fuente de nombres visibles evita que la barra y el rail se
// contradigan.
const SUITE_LABEL = {
  "inbox.all": "Todas",
  "inbox.unassigned": "Sin asignar",
  "inbox.mine": "Mis casos",
  "inbox.sla": "Vencidos (SLA)",
  "inbox.ai": "Atendidas por IA",
  "inbox.ai_live": "IA en curso",
  "inbox.resolved": "Resueltos",
  "channel.whatsapp": "WhatsApp",
  "channel.messenger": "Messenger",
  "channel.instagram": "Instagram",
  "channel.portal": "Portal",
  "channel.groups": "Grupos",
  "suite.contacts": "Contactos",
  "suite.settings": "Canales",
  "suite.quickReplies": "Respuestas rápidas",
  "suite.reports": "Reportes",
  "suite.ai": "Asistente IA",
  "suite.internal": "Asistente interno",
};

// Misma derivación por pathname que hace el rail cuando la ruta no pasa
// `activeKey` (Reportes y Asistente IA no lo pasan).
function resolveSectionKey(activeKey, pathname) {
  if (activeKey) return activeKey;
  if (pathname.startsWith("/admin/helpdesk/contacts")) return "suite.contacts";
  if (pathname.startsWith("/admin/helpdesk/settings")) return "suite.settings";
  if (pathname.startsWith("/admin/helpdesk/quick-replies")) return "suite.quickReplies";
  if (pathname.startsWith("/admin/helpdesk/reports")) return "suite.reports";
  if (pathname.startsWith("/admin/helpdesk/ai")) return "suite.ai";
  if (pathname.startsWith("/admin/helpdesk/interno")) return "suite.internal";
  return "inbox.unassigned";
}

/**
 * ¿El evento lleva un modificador que invalida el atajo? En teclados
 * latinoamericanos "[" y "]" se escriben con AltGr, que llega como ctrl+alt: si
 * se descartara cualquier modificador, los atajos serían imposibles de usar ahí.
 * @param {KeyboardEvent} event
 * @returns {boolean}
 */
export function hasBlockingModifier(event) {
  if (event.metaKey) return true;
  if (event.ctrlKey && event.altKey) return false; // AltGr
  return event.ctrlKey || event.altKey;
}

/**
 * ¿El foco está escribiendo? Los atajos de una sola tecla no deben robarle
 * caracteres al redactor de mensajes ni a los buscadores.
 * @param {EventTarget} el
 * @returns {boolean}
 */
export function isTypingTarget(el) {
  if (!el) return false;
  return (
    !!el.isContentEditable ||
    ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName)
  );
}

/**
 * Elementos enfocables dentro de un contenedor, en orden de tabulación. Mismo
 * criterio que ui/Modal: el drawer necesita atrapar el foco igual que un modal.
 * @param {Element|null} root
 * @returns {Element[]}
 */
function getFocusable(root) {
  if (!root) return [];
  const selectors = [
    'a[href]',
    'area[href]',
    'input:not([disabled]):not([type="hidden"])',
    "select:not([disabled])",
    "textarea:not([disabled])",
    "button:not([disabled])",
    "iframe",
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
  ];
  return Array.from(root.querySelectorAll(selectors.join(","))).filter(
    (el) => !el.hasAttribute("disabled") && el.tabIndex !== -1
  );
}

// Shell común de las tres rutas helpdesk: /admin/helpdesk, /settings, /contacts.
// Renderiza un wrapper con clase .helpdesk para activar el modo full-height del
// shell admin (mainLayout.scss → :has(.helpdesk)) y aloja:
//   - Rail propio (HelpdeskRail) a la izquierda en desktop, drawer en mobile.
//   - Slot principal en `children` (ocupa flex-1).
//
// Props:
//   activeKey: override de qué ítem del rail está activo. Si no se pasa, el
//              rail lo deriva del pathname (ver HelpdeskRail).
//   counters:  contadores locales para el rail (calculados por el inbox).
//   onSelectInboxKey(key): callback que recibe la sub-view elegida desde el rail.
//   channelAccounts: cuentas/números vinculados por canal. Si la vista no las
//              pasa (Reportes, Contactos, …) el layout las pide él mismo: el
//              rail las necesita para decidir qué canales mostrar. null =
//              desconocidas (fallo al pedirlas): el rail muestra todos.
//   accountFilter / onSelectAccount(key, account): filtro por cuenta del inbox.
//   onSelectRetiredAccount(account): abrir la vista de un número retirado
//              (inboxKey 'retired.<id>').
//   onAccountsChanged(): se retiró o migró un número desde el rail.
//   forceRailCollapsed: override visual (lo usa el modo foco del inbox). NO toca
//              la preferencia persistida del rail: al soltarlo, el rail vuelve
//              exactamente al estado que el usuario había elegido.
//   children:  contenido de la ruta actual.
export default function HelpdeskSuiteLayout({
  activeKey,
  counters,
  onSelectInboxKey,
  channelAccounts,
  accountFilter,
  onSelectAccount,
  // Carpetas del helpdesk: lista y handlers de CRUD/selección; pasan directo
  // al rail (desktop y drawer).
  folders,
  onSelectFolder,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  // Números retirados: abrir la vista de uno, y aviso de que las cuentas
  // cambiaron (retiro/migración desde el rail) para que la vista refresque.
  onSelectRetiredAccount,
  onAccountsChanged,
  forceRailCollapsed = false,
  onReleaseRailForce,
  children,
}) {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  // Vistas de la suite que no traen sus propias cuentas: se piden acá para que
  // el rail muestre los mismos canales en todas las rutas.
  const ownsAccounts = channelAccounts === undefined;
  const [fetchedAccounts, setFetchedAccounts] = useState(
    () => cachedChannelAccounts() ?? []
  );
  useEffect(() => {
    if (!ownsAccounts) return undefined;
    let cancelled = false;
    fetchChannelAccounts()
      .then((accounts) => {
        if (!cancelled) setFetchedAccounts(accounts);
      })
      .catch(() => {
        if (!cancelled) setFetchedAccounts(null);
      });
    return () => {
      cancelled = true;
    };
  }, [ownsAccounts]);
  const railAccounts = ownsAccounts ? fetchedAccounts : channelAccounts;

  // A6 · Etiqueta de la sección activa para la barra móvil. Las carpetas llegan
  // como `folder.<id>` y su nombre sale de `folders`, que el layout ya recibe.
  const sectionKey = resolveSectionKey(activeKey, pathname);
  const sectionLabel = (() => {
    if (sectionKey.startsWith("folder.")) {
      const id = sectionKey.slice("folder.".length);
      const folder = (folders ?? []).find((f) => String(f?.id) === id);
      return folder?.name || t("Carpeta");
    }
    if (sectionKey.startsWith("retired.")) {
      const id = sectionKey.slice("retired.".length);
      const account = (railAccounts ?? []).find((a) => String(a?.id) === id);
      const phone = formatPhone(account?.phone);
      return phone
        ? t("Número retirado {{numero}}").replace("{{numero}}", phone)
        : t("Número retirado");
    }
    const label = SUITE_LABEL[sectionKey];
    return label ? t(label) : t("Mesa de ayuda");
  })();

  // Colapso persistido para no reiniciarlo entre navegaciones ni recargas.
  const [collapsed, setCollapsed] = usePersistentState("rail.collapsed", false);
  // Mientras un override externo (modo foco) mantiene el rail comprimido, el
  // botón y el atajo "[" NO pueden quedar muertos: pedir el rail de vuelta
  // suelta el override en vez de mutar una preferencia que no se vería.
  const releaseRef = useRef(onReleaseRailForce);
  releaseRef.current = onReleaseRailForce;
  const toggleCollapsed = useCallback(() => {
    if (forceRailCollapsed && releaseRef.current) {
      releaseRef.current();
      return;
    }
    setCollapsed((c) => !c);
  }, [forceRailCollapsed, setCollapsed]);

  // Drawer mobile: abierto/cerrado, independiente del colapso desktop.
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Cerrar drawer al navegar a otra sub-view (cuando el rail llama onSelectInboxKey).
  const handleInboxSelect = (key) => {
    setDrawerOpen(false);
    onSelectInboxKey?.(key);
  };
  const handleAccountSelect = (key, account) => {
    setDrawerOpen(false);
    onSelectAccount?.(key, account);
  };
  const handleFolderSelect = (folderId) => {
    setDrawerOpen(false);
    onSelectFolder?.(folderId);
  };
  const handleRetiredSelect = (account) => {
    setDrawerOpen(false);
    onSelectRetiredAccount?.(account);
  };

  // Retirar o migrar un número. El modal vive acá (una sola instancia) y no en
  // el rail: el shell monta dos rails (desktop y drawer).
  const [manageTargetId, setManageTargetId] = useState(null);
  const handleManageAccount = (account) => {
    setDrawerOpen(false);
    setManageTargetId(account?.id ?? null);
  };
  const handleRetired = () => {
    // Las cuentas cambian (el número sale, o pasa a "Números retirados"): el
    // rail propio se refresca acá y la vista dueña de las cuentas, por su lado.
    if (ownsAccounts) {
      fetchChannelAccounts()
        .then(setFetchedAccounts)
        .catch(() => setFetchedAccounts(null));
    }
    onAccountsChanged?.();
  };

  // Atajos del shell: "[" alterna el rail (vale en las tres rutas helpdesk) y
  // Escape cierra el drawer. Escape se atiende siempre; el resto se ignora
  // mientras el foco está escribiendo, para no robar teclas al redactar.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.defaultPrevented || hasBlockingModifier(event)) return;
      if (event.key === "Escape") {
        setDrawerOpen(false);
        return;
      }
      if (isTypingTarget(event.target)) return;
      if (event.key === "[") {
        event.preventDefault();
        toggleCollapsed();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [toggleCollapsed]);

  // Drawer = diálogo modal: con aria-modal="true" el lector de pantalla oculta
  // el resto de la página, así que el foco NO puede quedarse detrás del overlay.
  // Mismo patrón que ui/Modal: foco inicial adentro, ciclo de Tab acotado,
  // devolución del foco al cerrar y scroll del body bloqueado mientras abre.
  const drawerRef = useRef(null);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const previouslyFocused = document.activeElement;
    const focusables = getFocusable(drawerRef.current);
    (focusables[0] || drawerRef.current)?.focus?.();

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = prevOverflow;
      if (previouslyFocused?.focus) {
        try {
          previouslyFocused.focus();
        } catch {
          /* el disparador pudo desmontarse: no hay a dónde devolver el foco */
        }
      }
    };
  }, [drawerOpen]);

  const handleDrawerKeyDown = useCallback((event) => {
    if (event.key !== "Tab") return;
    const focusables = getFocusable(drawerRef.current);
    if (focusables.length === 0) {
      event.preventDefault();
      drawerRef.current?.focus?.();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (event.shiftKey) {
      if (active === first || !drawerRef.current?.contains(active)) {
        event.preventDefault();
        last.focus();
      }
    } else if (active === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  return (
    <div className="helpdesk helpdesk-suite flex w-full h-full min-h-0 bg-bg">
      {/* Rail desktop (≥md) */}
      <div className="hidden md:flex h-full">
        <HelpdeskRail
          navId="helpdesk-rail-nav"
          activeKey={activeKey}
          counters={counters}
          onSelectInboxKey={onSelectInboxKey}
          channelAccounts={railAccounts}
          accountFilter={accountFilter}
          onSelectAccount={onSelectAccount}
          folders={folders}
          onSelectFolder={onSelectFolder}
          onCreateFolder={onCreateFolder}
          onRenameFolder={onRenameFolder}
          onDeleteFolder={onDeleteFolder}
          onSelectRetiredAccount={onSelectRetiredAccount}
          onManageAccount={handleManageAccount}
          collapsed={collapsed || forceRailCollapsed}
          onToggleCollapsed={toggleCollapsed}
        />
      </div>

      {/* Drawer mobile */}
      {drawerOpen && (
        <div
          ref={drawerRef}
          tabIndex={-1}
          onKeyDown={handleDrawerKeyDown}
          /* A6 · zIndex 1200 = banda de overlays (src/scss/_bottom-nav.scss:8-26).
             Con `z-40` el drawer quedaba DEBAJO del header de la app (150) y de
             la bottom-nav (1000): su X de cerrar caía sobre el conmutador de
             idioma y el primer ítem del rail salía cortado por arriba. */
          style={{ zIndex: 1200 }}
          className="md:hidden fixed inset-0 flex focus:outline-none"
          role="dialog"
          aria-modal="true"
          aria-label={t("Navegación de mesa de ayuda")}
        >
          <div
            className="absolute inset-0"
            style={{ background: "var(--overlay-backdrop)" }}
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <div className="relative z-10 h-full animate-in">
            <HelpdeskRail
              navId="helpdesk-rail-nav-mobile"
              activeKey={activeKey}
              counters={counters}
              onSelectInboxKey={handleInboxSelect}
              channelAccounts={railAccounts}
              accountFilter={accountFilter}
              onSelectAccount={handleAccountSelect}
              folders={folders}
              onSelectFolder={handleFolderSelect}
              onCreateFolder={onCreateFolder}
              onRenameFolder={onRenameFolder}
              onDeleteFolder={onDeleteFolder}
              onSelectRetiredAccount={handleRetiredSelect}
              onManageAccount={handleManageAccount}
              collapsed={false}
              onToggleCollapsed={() => setDrawerOpen(false)}
            />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              aria-label={t("Cerrar")}
              className="absolute top-3 right-3 inline-flex w-11 h-11 items-center justify-center rounded-md text-fg-muted hover:bg-bg-muted hover:text-fg"
            >
              <X className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      {/* Slot principal */}
      <main className="flex-1 min-w-0 flex flex-col bg-bg-muted/30">
        {/* Barra de navegación mobile (<md): el toggle vive en su propia fila,
            no flotando, para no tapar el contenido de las rutas. */}
        <div className="md:hidden flex items-center h-11 px-2 border-b border-border bg-bg shrink-0">
          {/* A6 · La fila ya no es una hamburguesa muda: es el conmutador de
              bandeja y dice en cuál estás (dato que en escritorio da el rail
              siempre visible). Mismo onClick de antes, a todo el ancho.
              aria-controls solo con el drawer abierto: su nav no existe en el
              DOM mientras está cerrado y la referencia quedaría rota. */}
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label={t("Cambiar de bandeja")}
            aria-expanded={drawerOpen}
            aria-controls={drawerOpen ? "helpdesk-rail-nav-mobile" : undefined}
            className="flex-1 min-w-0 inline-flex items-center gap-2 h-11 px-2 rounded-md text-fg-muted hover:text-fg hover:bg-bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors"
          >
            {/* Bandeja y no hamburguesa: el header de la app ya monta un ≡
                44px más arriba y dos hamburguesas apiladas leían como dos
                menús del mismo tipo. El icono + el nombre + el chevron dicen
                que esto elige BANDEJA. */}
            <Inbox className="w-5 h-5 shrink-0" aria-hidden="true" />
            <span className="min-w-0 flex-1 truncate text-sm font-semibold text-fg text-left">
              {sectionLabel}
            </span>
            <ChevronDown className="w-4 h-4 shrink-0" aria-hidden="true" />
          </button>
        </div>
        {children}
      </main>

      <RetireNumberModal
        isOpen={!!manageTargetId}
        integrationId={manageTargetId}
        onClose={() => setManageTargetId(null)}
        onRetired={handleRetired}
      />
    </div>
  );
}
